import React, { useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Checkbox } from 'common/components/antd/Checkbox/Checkbox';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Space } from 'common/components/antd/Space/Space';
import { Body, Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { KETO_READ_HOST, KETO_WRITE_HOST, KRATOS_ADMIN_HOST } from 'common/constants/kratos.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { IBANFormItemRules } from 'common/utils/form.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import { isValidUrl } from 'common/utils/url-validator.util';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import {
  useCreateCounterpartyMutation,
  useUpdateCounterpartyMutation,
} from 'domains/counterparties/queries/counterparty.query';
import {
  TCreateCounterpartyReqData,
  TUpdateCounterpartyReqData
} from 'domains/counterparties/types/counterparty.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SFooter, SEyesReview } from './modal.style';
import { EModalType } from '../enums/conterparties.enums';
import { TCounterpartyModalData } from '../types/counterparties.types';

export enum ECounterpartyFieldKey {
  HoldingName = 'holding_name',
  Name = 'name',
  Code = 'code',
  Segment = 'segment',
  ProcessingIban = 'processing_iban',
  EmissionIban = 'emission_iban',
  Description = 'description',
  Logo = 'logo_url',
  UserAuthUrl = 'user_auth_url',
  UserKeyUrl = 'user_key_url',
  UserIdUrl = 'user_id_url',
  Asset = 'asset',
  KeyKeeperLogin = 'key_keeper_login',
  OneCustomerAcc = 'one_customer_acc',
  RecoveryFrontendUrl = 'recovery_frontend_url',
  CallbackUrl = 'callback_url',
}

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TCounterpartyModalData;
};

const CreateEditCounterpartyModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: { rowData, type: modalType, counterpartiesData },
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form] = Form.useForm();

  const {
    data: assetsData,
    isLoading: isAssetsDataLoading,
    isError: isAssetsDataError,
    error: assetsDataError,
  } = useGetAssetsListQuery(
    ENV_COUNTERPARTY_GUID,
    {},
    {
      enabled: modalType === EModalType.Create,
    },
  );

  const { mutateAsync: mutateAsyncCreateCounterpartyMutation } = useCreateCounterpartyMutation();
  const { mutateAsync: mutateAsyncUpdateCounterpartyMutation } = useUpdateCounterpartyMutation();

  useEffect(() => {
    if (isAssetsDataError) {
      console.error(assetsDataError);
    }
  }, [isAssetsDataError, assetsDataError]);

  useEffect(() => {
    if (Object.keys(rowData || {}).length && modalType === EModalType.Edit) {
      const {
        description,
        holding_name,
        name,
        code,
        one_customer_acc,
        logo_url,
        callback_url,
      } = rowData;

      form.setFieldsValue({
        [ECounterpartyFieldKey.HoldingName]: holding_name,
        [ECounterpartyFieldKey.Name]: name,
        [ECounterpartyFieldKey.Code]: code,
        [ECounterpartyFieldKey.Description]: description,
        [ECounterpartyFieldKey.OneCustomerAcc]: one_customer_acc,
        [ECounterpartyFieldKey.Logo]: logo_url,
        [ECounterpartyFieldKey.CallbackUrl]: callback_url,
      });
    }
  }, [rowData]);

  const assetsFieldOptionsList = assetsData?.stellar_assets.map((assetItem) => ({
    label: assetItem.asset_code,
    value: assetItem.asset_issuer,
  }));

  const segmentOptions = [
    {
      label: 'PE',
      value: 1,
    },
    {
      label: 'PI',
      value: 2,
    },
  ]; // mock

  const onCloseModalHandler = (): void => {
    form.resetFields();
    onCancelModalHandler();
  };

  const onFormSubmitHandler = async (formValues: Record<ECounterpartyFieldKey, string>): Promise<void> => {
    try {
      setIsLoading(true);

      const {
        holding_name,
        name,
        code,
        description,
        user_auth_url,
        user_key_url,
        user_id_url,
        asset: assetIssuer,
        processing_iban,
        emission_iban,
        key_keeper_login,
        one_customer_acc,
        recovery_frontend_url,
        logo_url,
        callback_url,
      } = formValues;

      if (modalType === EModalType.Create) {
        const assetCode = assetsFieldOptionsList?.filter((optionItem) => optionItem.value === assetIssuer)[0].label;

        const requestData: TCreateCounterpartyReqData = {
          asset_code: assetCode || '',
          asset_issuer: assetIssuer,
          description,
          holding_name,
          keto_read_host: KETO_READ_HOST,
          keto_write_host: KETO_WRITE_HOST,
          key_keeper_login,
          kratos_admin_host: KRATOS_ADMIN_HOST,
          name,
          code,
          recovery_frontend_url,
          user_auth_url,
          user_id_url,
          user_key_url,
          processing_iban,
          emission_iban,
          one_customer_acc,
          logo_url,
          callback_url,
        };

        await mutateAsyncCreateCounterpartyMutation({ requestData });
        notifySuccess('usrRoleMng.counterparties.modal.create.notification.success');
      } else {
        const { guid: counterpartyGUID } = counterpartiesData.counterparties[0];

        const requestData: TUpdateCounterpartyReqData = {
          description,
          holding_name,
          name,
          code,
          one_customer_acc,
          logo_url,
          callback_url,
        };

        await mutateAsyncUpdateCounterpartyMutation({ counterpartyGUID, requestData });
        notifySuccess('usrRoleMng.counterparties.modal.edit.notification.success');
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList._def,
      });
      setIsLoading(false);
      navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES);
      onCloseModalHandler();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <EnhancedModal width={480} open={isOpen} onCancel={onCloseModalHandler}>
      <Form
        form={form}
        onFinish={onFormSubmitHandler}
        initialValues={{
          [ECounterpartyFieldKey.RecoveryFrontendUrl]: `${window.location.origin}/reset-password`,
        }}
      >
        <Header>
          {modalType === EModalType.Create
            ? t('usrRoleMng.counterparties.modal.create.title')
            : t('usrRoleMng.counterparties.modal.edit.title')}
        </Header>
        <Body>
          <Space size={16} direction="vertical">
            <Form.Item
              name={ECounterpartyFieldKey.HoldingName}
              label={t('usrRoleMng.counterparties.modal.create.form.field.holdingName.label')}
            >
              <Input
                placeholder={t('usrRoleMng.counterparties.modal.create.form.field.holdingName.placeholder') as string}
              />
            </Form.Item>

            <Form.Item
              name={ECounterpartyFieldKey.Name}
              label={t('usrRoleMng.counterparties.modal.create.form.field.name.label')}
            >
              <Input placeholder={t('usrRoleMng.counterparties.modal.create.form.field.name.placeholder') as string} />
            </Form.Item>

            <Form.Item
              name={ECounterpartyFieldKey.Code}
              label={t('usrRoleMng.counterparties.modal.create.form.field.code.label')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('usrRoleMng.counterparties.modal.create.form.field.code.placeholder') as string} />
            </Form.Item>

            {modalType === EModalType.Create && (
              <>
                <Form.Item
                  name={ECounterpartyFieldKey.Segment}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.segment.label')}
                >
                  <Select
                    style={{
                      width: '100%',
                    }}
                    placeholder={t('usrRoleMng.counterparties.modal.create.form.field.segment.placeholder') as string}
                    options={segmentOptions}
                  />
                </Form.Item>

                <Form.Item
                  rules={IBANFormItemRules}
                  name={ECounterpartyFieldKey.ProcessingIban}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.processingIban.label')}
                >
                  <Input
                    placeholder={t('usrRoleMng.counterparties.modal.create.form.field.iban.placeholder') as string}
                  />
                </Form.Item>

                <Form.Item
                  rules={IBANFormItemRules}
                  name={ECounterpartyFieldKey.EmissionIban}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.emissionIban.label')}
                >
                  <Input
                    placeholder={t('usrRoleMng.counterparties.modal.create.form.field.iban.placeholder') as string}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              name={ECounterpartyFieldKey.Description}
              label={t('usrRoleMng.counterparties.modal.create.form.field.description.label')}
            >
              <Input.TextArea
                placeholder={t('usrRoleMng.counterparties.modal.create.form.field.description.placeholder') as string}
                autoSize={{ minRows: 4, maxRows: 10 }}
              />
            </Form.Item>

            <Form.Item
              name={ECounterpartyFieldKey.Logo}
              label={t('usrRoleMng.counterparties.modal.create.form.field.logo.label')}
              extra={t('usrRoleMng.counterparties.modal.create.form.field.logo.hint')}
              rules={[
                (): { validator(_: any, value: string): Promise<any> } => ({
                  validator(_, value): Promise<any> {
                    if (value && !isValidUrl(value)) {
                      return Promise.reject(
                        new Error(
                          t('usrRoleMng.counterparties.modal.create.form.field.logo.validation.invalidUrl') as string
                        )
                      );
                    }

                    if (value && (!value.toLowerCase().endsWith('.svg') && !value.toLowerCase().endsWith('.png'))) {
                      return Promise.reject(
                        new Error(
                    t('usrRoleMng.counterparties.modal.create.form.field.logo.validation.invalidFileFormat') as string
                        )
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder={t('usrRoleMng.counterparties.modal.create.form.field.logo.placeholder') as string}
              />
            </Form.Item>

            {modalType === EModalType.Create && (
              <>
                <Form.Item
                  name={ECounterpartyFieldKey.UserAuthUrl}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.userAuthUrl.label')}
                >
                  <Input
                    placeholder={
                      t('usrRoleMng.counterparties.modal.create.form.field.userAuthUrl.placeholder') as string
                    }
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.UserIdUrl}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.userIdUrl.label')}
                >
                  <Input
                    placeholder={t('usrRoleMng.counterparties.modal.create.form.field.userIdUrl.placeholder') as string}
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.UserKeyUrl}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.userKeyUrl.label')}
                >
                  <Input
                    placeholder={
                      t('usrRoleMng.counterparties.modal.create.form.field.userKeyUrl.placeholder') as string
                    }
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.RecoveryFrontendUrl}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.recoveryFrontendUrl.label')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'usrRoleMng.counterparties.modal.create.form.field.recoveryFrontendUrl.validation.required',
                      ) as string,
                    },
                    (): { validator(_: any, value: any): Promise<void> } => ({
                      validator(_, value): Promise<void> {
                        const hostValid = t(
                          'usrRoleMng.counterparties.modal.create.form.field.recoveryFrontendUrl.validation.host',
                        ) as string;

                        if (!isValidUrl(value) && value) {
                          return Promise.reject(new Error(hostValid));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={
                      t('usrRoleMng.counterparties.modal.create.form.field.recoveryFrontendUrl.placeholder') as string
                    }
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.Asset}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.asset.label')}
                >
                  <Select
                    style={{
                      width: '100%',
                    }}
                    placeholder={t('usrRoleMng.counterparties.modal.create.form.field.asset.placeholder') as string}
                    loading={isAssetsDataLoading}
                    options={assetsFieldOptionsList}
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.KeyKeeperLogin}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.keyKeeperLogin.label')}
                  rules={[{ required: true }]}
                >
                  <Input
                    placeholder={
                      t('usrRoleMng.counterparties.modal.create.form.field.keyKeeperLogin.placeholder') as string
                    }
                  />
                </Form.Item>

                <Form.Item
                  name={ECounterpartyFieldKey.CallbackUrl}
                  label={t('usrRoleMng.counterparties.modal.create.form.field.CallbackUrl.label')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'usrRoleMng.counterparties.modal.create.form.field.CallbackUrl.requiredField',
                      ) as string,
                    },
                    (): { validator(_: any, value: any): Promise<void> } => ({
                      validator(_, value): Promise<void> {
                        const hostValid = t(
                          'usrRoleMng.counterparties.modal.create.form.field.CallbackUrl.validationURL',
                        ) as string;

                        if (!isValidUrl(value) && value) {
                          return Promise.reject(new Error(hostValid));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={
                      t('usrRoleMng.counterparties.modal.create.form.field.CallbackUrl.placeholder') as string
                    }
                  />
                </Form.Item>
              </>
            )}

            <Form.Item name={ECounterpartyFieldKey.OneCustomerAcc} valuePropName="checked">
              <Checkbox>{t('usrRoleMng.counterparties.modal.create.form.checkBox.oneCustomerAcc.label')}</Checkbox>
            </Form.Item>
          </Space>

          <SEyesReview>
            <div>{t('usrRoleMng.counterparties.modal.create.form.checkBox.forEyesReview.label')}</div>
            <div>
              <Checkbox style={{ marginRight: 32 }}>
                {t('usrRoleMng.counterparties.modal.create.form.checkBox.send')}
              </Checkbox>
              <Checkbox>{t('usrRoleMng.counterparties.modal.create.form.checkBox.receive')}</Checkbox>
            </div>
          </SEyesReview>
        </Body>
        <Footer>
          <SFooter>
            <Button style={{ marginRight: 12 }} onClick={onCloseModalHandler}>
              {t('usrRoleMng.counterparties.modal.create.button.cancel')}
            </Button>
            <Button loading={isLoading} htmlType="submit" type="primary">
              {modalType === EModalType.Create
                ? t('usrRoleMng.counterparties.modal.create.button.create')
                : t('usrRoleMng.counterparties.modal.edit.button.save')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateEditCounterpartyModal;
